import { useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { RootState } from '@/store';
import { resetUnsavedChanges, setIsBlocking } from '@/store/unsavedChanges';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationGuardProps {
  children: React.ReactNode;
}

export const NavigationGuard: React.FC<NavigationGuardProps> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleConfirm } = useConfirm();

  const { hasUnsavedChanges, isBlocking } = useSelector((state: RootState) => state.unsavedChanges);

  // Intercept all navigation attempts
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (!link) return;

      const href = link.getAttribute('href') || '';

      // Skip special links
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('javascript:') ||
        href.startsWith('mailto:')
      ) {
        return;
      }

      // Ignore links that should not be blocked
      const isExternal = href.startsWith('http') || href.startsWith('//');
      const isBlobOrData =
        href.startsWith('blob:') || href.startsWith('data:') || href.startsWith('about:');
      const isDownload = link.hasAttribute('download');
      const isOpenNewTab = link.getAttribute('target') === '_blank';
      const isSkipGuard = (link.dataset && link.dataset.skipGuard === 'true') || false;

      if (isExternal || isBlobOrData || isDownload || isOpenNewTab || isSkipGuard) {
        return;
      }

      // Check if it's the same page
      if (href === location.pathname) {
        return;
      }

      // If there are unsaved changes, prevent navigation
      if (hasUnsavedChanges && !isBlocking) {
        event.preventDefault();

        dispatch(setIsBlocking(true));

        toggleConfirm({
          title: trans('plan.unsavedChangesTitle'),
          message: trans('plan.unsavedChangesMessage'),
          okText: trans('plan.leave'),
          cancelText: trans('plan.stay'),
          onOk: () => {
            dispatch(resetUnsavedChanges());
            dispatch(setIsBlocking(false));
            // Navigate to the intended destination
            navigate(href);
          },
          onClose: () => {
            dispatch(setIsBlocking(false));
          },
        });
      }
    };

    if (hasUnsavedChanges) {
      document.addEventListener('click', handleClick, true);
    }

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges, isBlocking, location.pathname, dispatch, navigate, toggleConfirm]);

  return <>{children}</>;
};
